import {TInterviewMessage} from "./TInterviewThread";
import InterviewThreadApi from "./InterviewThreadApi";

type TAudioFactory = (src: string) => HTMLAudioElement;

export default class VoicePlaybackController {

    private readonly audioUrls = new Map<number, Promise<string>>();
    private readonly createdUrls = new Set<string>();
    private readonly requestControllers = new Map<number, AbortController>();
    private audio: HTMLAudioElement|null = null;
    private playbackGeneration = 0;
    private triggeredByAutoPlayback = false;
    private destroyed = false;
    private autoPlaybackEnabled = false;
    private playingMessageId: number|null = null;

    constructor(
        private readonly api: InterviewThreadApi,
        private readonly threadId: number,
        private readonly enabled: boolean,
        private readonly onChange: () => void,
        private readonly onError: () => void,
        private readonly audioFactory: TAudioFactory = src => new Audio(src),
    ) {
    }

    public isSupported(): boolean {
        return this.enabled
            && typeof window !== "undefined"
            && typeof Audio === "function";
    }

    public isPlaying(messageId: number): boolean {
        return this.playingMessageId === messageId;
    }

    public isAutoPlaybackEnabled(): boolean {
        return this.autoPlaybackEnabled;
    }

    public getPlayingMessageId(): number|null {
        return this.playingMessageId;
    }

    public async toggle(message: TInterviewMessage): Promise<void> {
        if (!this.canPlay(message)) {
            return;
        }

        if (this.playingMessageId === message.id) {
            this.stop();
            this.onChange();
            return;
        }

        await this.play(message);
    }

    public async toggleAutoPlayback(messages: TInterviewMessage[]): Promise<void> {
        if (!this.isSupported()) {
            return;
        }

        if (this.autoPlaybackEnabled) {
            this.autoPlaybackEnabled = false;
            this.stop();
            this.onChange();
            return;
        }

        this.autoPlaybackEnabled = true;
        this.onChange();
        const lastAgentMessage = this.getLastAgentMessage(messages);
        if (lastAgentMessage !== null) {
            await this.play(lastAgentMessage, true);
        }
    }

    public playLatestNewAgentMessage(messages: TInterviewMessage[], previousMessageIds: Set<number>): void {
        if (!this.autoPlaybackEnabled) {
            return;
        }

        for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i];
            if (message.role === "agent" && message.speechAvailable && !previousMessageIds.has(message.id)) {
                void this.play(message, true);
                return;
            }
        }
    }

    public async play(message: TInterviewMessage, triggeredByAutoPlayback = false): Promise<void> {
        if (!this.canPlay(message)) {
            return;
        }

        this.stop();
        const generation = this.playbackGeneration;
        this.triggeredByAutoPlayback = triggeredByAutoPlayback;
        this.playingMessageId = message.id;
        this.onChange();

        try {
            const url = await this.getAudioUrl(message.id);
            if (this.destroyed || generation !== this.playbackGeneration) {
                return;
            }

            const audio = this.audioFactory(url);
            audio.preload = "none";
            audio.onended = () => this.finish(message.id, generation);
            audio.onerror = () => this.fail(message.id, generation);
            this.audio = audio;
            await audio.play();
        } catch {
            this.fail(message.id, generation);
        }
    }

    public stop(): void {
        this.playbackGeneration++;
        if (this.audio !== null) {
            this.audio.onended = null;
            this.audio.onerror = null;
            this.audio.pause();
            this.audio.removeAttribute("src");
            this.audio.load();
        }

        this.audio = null;
        this.triggeredByAutoPlayback = false;
        this.playingMessageId = null;
    }

    public destroy(): void {
        this.destroyed = true;
        this.stop();
        this.requestControllers.forEach(controller => controller.abort());
        this.requestControllers.clear();
        this.createdUrls.forEach(url => URL.revokeObjectURL(url));
        this.createdUrls.clear();
        this.audioUrls.clear();
    }

    private getAudioUrl(messageId: number): Promise<string> {
        const cached = this.audioUrls.get(messageId);
        if (cached !== undefined) {
            return cached;
        }

        const requestController = new AbortController();
        this.requestControllers.set(messageId, requestController);
        const request = this.api.synthesizeVoiceMessage(
            this.threadId,
            messageId,
            requestController.signal,
        )
            .then(blob => {
                const url = URL.createObjectURL(blob);
                if (this.destroyed) {
                    URL.revokeObjectURL(url);
                    throw new Error("Voice playback controller was destroyed.");
                }
                this.createdUrls.add(url);

                return url;
            })
            .catch(error => {
                this.audioUrls.delete(messageId);
                throw error;
            })
            .finally(() => {
                if (this.requestControllers.get(messageId) === requestController) {
                    this.requestControllers.delete(messageId);
                }
            });
        this.audioUrls.set(messageId, request);

        return request;
    }

    private finish(messageId: number, generation: number): void {
        if (generation !== this.playbackGeneration || this.playingMessageId !== messageId) {
            return;
        }

        this.audio = null;
        this.triggeredByAutoPlayback = false;
        this.playingMessageId = null;
        this.onChange();
    }

    private fail(messageId: number, generation: number): void {
        if (generation !== this.playbackGeneration || this.playingMessageId !== messageId) {
            return;
        }

        this.audio = null;
        this.playingMessageId = null;
        if (this.triggeredByAutoPlayback) {
            this.autoPlaybackEnabled = false;
        }
        this.triggeredByAutoPlayback = false;
        this.onError();
        this.onChange();
    }

    private canPlay(message: TInterviewMessage): boolean {
        return this.isSupported()
            && message.role === "agent"
            && message.speechAvailable;
    }

    private getLastAgentMessage(messages: TInterviewMessage[]): TInterviewMessage|null {
        for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i];
            if (message.role === "agent" && message.speechAvailable) {
                return message;
            }
        }

        return null;
    }

}
